import type React from 'react';
import { useMemo, useState } from 'react';
import type { Conversation } from '../../../api/types';
import { groupByTimePeriod, formatTime } from '../../../util/dateUtils';
import { LoadingSpinner, EmptyState, Icon } from '../../shared';
import styles from './ChatHistoryPanel.module.css';

export interface ChatHistoryPanelProps {
  conversations: Conversation[];
  currentConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onNewChat?: () => void;
  isLoading?: boolean;
}

/**
 * ChatHistoryPanel Component
 *
 * Displays conversation history grouped by date (Today, Yesterday, etc.)
 */
export const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewChat,
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) => conv.title?.toLowerCase().includes(query));
  }, [conversations, searchQuery]);

  const groupedConversations = useMemo(() => {
    return groupByTimePeriod(filteredConversations, (conv) => conv.created_at);
  }, [filteredConversations]);



  if (isLoading) {
    return (
      <div className={styles.panel}>
        <LoadingSpinner message="Loading conversations..." />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.searchWrapper}>
        <div className={styles.searchContainer}>
          <Icon name="search" size={18} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.listContainer}>
        {conversations.length === 0 ? (
          <EmptyState
            message="No conversation history yet"
            action={
              onNewChat && (
                <button className={styles.primaryButton} onClick={onNewChat}>
                  Start a New Chat
                </button>
              )
            }
          />
        ) : filteredConversations.length === 0 ? (
          <EmptyState
            variant="compact"
            message={searchQuery ? 'No conversations found matching your search' : 'No conversations'}
          />
        ) : (
          <div className={styles.list}>
            {Object.entries(groupedConversations).map(([group, convs]) => {
              if (convs.length === 0) return null;

              return (
                <div key={group} className={styles.group}>
                  <h4 className={styles.groupTitle}>{group}</h4>
                  {convs.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      className={`${styles.item} ${
                        currentConversationId === conv.conversation_id ? styles.active : ''
                      }`}
                      onClick={() => onSelectConversation(conv.conversation_id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectConversation(conv.conversation_id);
                        }
                      }}
                    >
                      <div className={styles.itemContent}>
                        <div className={styles.itemHeader}>
                          <span className={styles.itemTitle}>
                            {conv.title || 'Untitled Conversation'}
                          </span>
                        </div>
                        <span className={styles.itemTime}>{formatTime(conv.created_at)}</span>
                      </div>
                      {/* {currentConversationId === conv.conversation_id && (
                        <button
                          className={styles.itemDelete}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          aria-label="Delete conversation"
                          title="Delete conversation"
                        >
                          <Icon name="delete" size={16} />
                        </button>
                      )} */}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
